import { useState } from 'react'
import { Card, Accordion, Badge, Spinner, Pagination } from 'react-bootstrap'
import { useAdminUserUsage } from 'api/hooks/use-admin-user'

const formatEventName = (name) =>
  name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const formatTime = (time) => {
  if (!time) return '--'
  return new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const formatDateTime = (time) => {
  if (!time) return '--'
  return new Date(time).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDuration = (events) => {
  if (!events || events.length < 2) return null
  const first = new Date(events[0].time)
  const last = new Date(events[events.length - 1].time)
  const diffMs = last - first
  if (diffMs < 0) return null
  const diffMins = Math.floor(diffMs / 60000)
  const diffSecs = Math.floor((diffMs % 60000) / 1000)
  if (diffMins === 0) return `${diffSecs}s`
  return `${diffMins}m ${diffSecs}s`
}

const getEventVariant = (name) => {
  if (name === 'page_view') return 'page-view'
  if (/_created$|_uploaded$|_saved$/.test(name)) return 'created'
  if (/_updated$|_shared$/.test(name)) return 'updated'
  if (/_deleted$|_removed$|_cancelled$|_unshared$|_unsaved$/.test(name)) return 'deleted'
  return 'default'
}

const EventProperties = ({ properties }) => {
  if (!properties || Object.keys(properties).length === 0) return null

  const HIDDEN_KEYS = ['url', 'search', 'referrer']
  const entries = Object.entries(properties).filter(([k, v]) => v !== null && v !== undefined && !HIDDEN_KEYS.includes(k))
  if (entries.length === 0) return null

  return (
    <div className="d-flex flex-wrap gap-1 mt-1">
      {entries.map(([key, value]) => (
        <Badge
          key={key}
          bg="light"
          text="primary"
          className="fw-normal border"
          style={{ fontSize: '0.7rem' }}
        >
          {key}: {String(value)}
        </Badge>
      ))}
    </div>
  )
}

const EventTimeline = ({ events }) => {
  if (!events || events.length === 0) {
    return <p className="text-muted small mb-0">No events in this session.</p>
  }

  return (
    <div className="session-timeline">
      <div className="session-timeline__line" />
      {events.map((event) => {
        const variant = getEventVariant(event.name)
        return (
          <div key={event.id} className="session-timeline__event">
            <div className={`session-timeline__dot session-timeline__dot--${variant}`} />
            <div className="ps-2">
              <div className="d-flex align-items-center gap-2">
                <span className={`fw-semibold small session-timeline__label--${variant}`}>
                  {formatEventName(event.name)}
                </span>
                <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                  {formatTime(event.time)}
                </span>
              </div>
              <EventProperties properties={event.properties} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

const SessionCard = ({ session, eventKey }) => {
  const duration = formatDuration(session.events)

  return (
    <Accordion.Item eventKey={eventKey}>
      <Accordion.Header>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <div>
            <span className="fw-semibold">{formatDateTime(session.started_at)}</span>
            <span className="text-muted ms-2 small">
              {session.browser} &middot; {session.os}
              {session.device_type && ` \u00b7 ${session.device_type}`}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {duration && (
              <Badge bg="light" text="primary" className="border fw-normal">
                {duration}
              </Badge>
            )}
            <Badge bg="light" text="primary" className="border fw-normal">
              {session.events.length} event{session.events.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </div>
      </Accordion.Header>
      <Accordion.Body className="py-3">
        <EventTimeline events={session.events} />
      </Accordion.Body>
    </Accordion.Item>
  )
}

const UserSessionTimeline = ({ userId }) => {
  const [page, setPage] = useState(1)

  const { data, isLoading } = useAdminUserUsage({
    userId,
    endpoint: 'session_timeline',
    params: { page, per_page: 10 },
  })

  const sessions = data?.sessions ?? []
  const meta = data?.meta ?? {}
  const totalPages = meta.total_pages ?? 1

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <span className="ms-2 text-muted">Loading sessions...</span>
      </div>
    )
  }

  if (sessions.length === 0) {
    return (
      <Card body className="text-center text-muted">
        No sessions recorded for this user.
      </Card>
    )
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage)
    }
  }

  const renderPaginationItems = () => {
    const items = []
    const delta = 2
    const left = Math.max(2, page - delta)
    const right = Math.min(totalPages - 1, page + delta)

    items.push(
      <Pagination.Item key={1} active={page === 1} onClick={() => handlePageChange(1)}>
        1
      </Pagination.Item>
    )

    if (left > 2) items.push(<Pagination.Ellipsis key="left-ellipsis" disabled />)

    for (let i = left; i <= right; i++) {
      items.push(
        <Pagination.Item key={i} active={page === i} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      )
    }

    if (right < totalPages - 1) items.push(<Pagination.Ellipsis key="right-ellipsis" disabled />)

    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={page === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      )
    }

    return items
  }

  return (
    <div>
      <Accordion alwaysOpen={false}>
        {sessions.map((session, index) => (
          <SessionCard key={session.visit_id} session={session} eventKey={String(index)} />
        ))}
      </Accordion>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination size="sm">
            <Pagination.Prev disabled={page === 1} onClick={() => handlePageChange(page - 1)} />
            {renderPaginationItems()}
            <Pagination.Next
              disabled={page === totalPages}
              onClick={() => handlePageChange(page + 1)}
            />
          </Pagination>
        </div>
      )}

      {meta.total_count !== undefined && (
        <p className="text-muted text-center small mt-2">
          {meta.total_count} total session{meta.total_count !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  )
}

export default UserSessionTimeline
